import { useState, useEffect, useCallback } from 'react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Circle, ChevronLeft, ClipboardList, RefreshCw } from 'lucide-react'
import { employeeApi } from '../../lib/employeeApi'
import { compressPhoto } from '../../lib/photo'

function stepStyle(line: string): string {
  if (/^🧤|^🥽|^👷/.test(line)) return 'bg-red-50 border-red-200 text-red-900'
  if (/^🌡/.test(line)) return 'bg-amber-50 border-amber-200 text-amber-900'
  if (/^🧴/.test(line)) return 'bg-blue-50 border-blue-200 text-blue-900'
  if (/^⚠/.test(line)) return 'bg-orange-50 border-orange-200 text-orange-900'
  if (/^⏱/.test(line)) return 'bg-purple-50 border-purple-200 text-purple-900'
  if (/^💧/.test(line)) return 'bg-cyan-50 border-cyan-200 text-cyan-900'
  if (/^🪣/.test(line)) return 'bg-teal-50 border-teal-200 text-teal-900'
  if (/^🚜|^🧹|^🧽/.test(line)) return 'bg-green-50 border-green-200 text-green-900'
  return 'bg-gray-50 border-gray-200 text-gray-800'
}

interface Task {
  id: string; nom: string; zone1: string; zone2: string; zone3: string
  produit: string; photoProduit?: string | null; photoLieu?: string | null; consigne: string; frequence: string; ordre: number
  creneauAttribution: string
  assignedTo?: { id: string; name: string }[]
  isShared?: boolean
  validated: boolean; validatedBy: string | null; validatedAt: string | null
  periodKey: string
  formulaire?: string | null
  rotationActuelle?: string | null
  dateLimite?: string | null
  enRetard?: boolean
  creneauRetenu?: string
}

const FREQ_COLORS: Record<string, string> = {
  jour: 'bg-blue-100 text-blue-700',
  semaine: 'bg-purple-100 text-purple-700',
  mois: 'bg-amber-100 text-amber-700',
  trimestre: 'bg-lime-100 text-lime-700',
  semestre: 'bg-orange-100 text-orange-700',
  annee: 'bg-red-100 text-red-700',
  exceptionnelle: 'bg-pink-100 text-pink-700',
}

const FREQ_LABELS: Record<string, string> = {
  jour: 'Quotidien', semaine: 'Hebdo', mois: 'Mensuel', trimestre: 'Trimestriel',
  semestre: 'Semestriel', annee: 'Annuel', exceptionnelle: 'Signalé'
}

// Formulaire à ouvrir pour une tâche QSE
function formRoute(task: Task): string {
  if (task.formulaire === 'reception') return '/app/livraison'
  if (task.formulaire === 'temperatures_hebdo') return '/app/temperatures?moment=hebdo'
  return `/app/temperatures?moment=${task.creneauAttribution === 'apres_midi' ? 'soir' : 'matin'}`
}

export default function MobileTasks() {
  const navigate = useNavigate()
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState<string | null>(null)

  const user = JSON.parse(localStorage.getItem('employeeData') || '{}')
  const date = format(new Date(), 'yyyy-MM-dd')
  const establishmentId = user?.establishmentId || user?.establishment?.id || ''
  const employeeId = user?.id || ''

  const load = useCallback(async () => {
    if (!employeeId) return
    setLoading(true)
    try {
      const r = await employeeApi.get('/employee/me/tasks-today')
      setTasks(r.data.tasks || [])
    } catch { } finally { setLoading(false) }
  }, [employeeId])

  useEffect(() => { load() }, [load])

  const [photos, setPhotos] = useState<Record<string, string>>({})
  const [errorMsg, setErrorMsg] = useState('')

  async function pickPhoto(taskId: string, file?: File) {
    if (!file) return
    try {
      const data = await compressPhoto(file)
      setPhotos(p => ({ ...p, [taskId]: data }))
    } catch { setErrorMsg('Photo illisible, réessayez') }
  }

  const validate = async (task: Task) => {
    setValidating(task.id); setErrorMsg('')
    try {
      await employeeApi.post(`/employee/me/tasks/${task.id}/validate`, photos[task.id] ? { photo: photos[task.id] } : {})
      setTasks(prev => prev.map(t => t.id === task.id
        ? { ...t, validated: true, validatedAt: new Date().toISOString() }
        : t))
      setPhotos(p => { const n = { ...p }; delete n[task.id]; return n })
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.error || 'Validation impossible, réessayez')
    } finally { setValidating(null) }
  }

  const [freqTab, setFreqTab] = useState<'jour' | 'semaine' | 'mois' | 'semestre' | 'annee'>('jour')

  const FREQ_TABS = [
    { freq: 'jour'     as const, label: 'Journalières',  color: 'bg-blue-600',   inactive: 'border-blue-200 text-blue-700' },
    { freq: 'semaine'  as const, label: 'Hebdomadaires', color: 'bg-purple-600', inactive: 'border-purple-200 text-purple-700' },
    { freq: 'mois'     as const, label: 'Mensuelles',    color: 'bg-amber-500',  inactive: 'border-amber-200 text-amber-700' },
    { freq: 'semestre' as const, label: 'Semestrielles', color: 'bg-orange-500', inactive: 'border-orange-200 text-orange-700' },
    { freq: 'annee'    as const, label: 'Annuelles',     color: 'bg-red-500',    inactive: 'border-red-200 text-red-700' },
  ]

  const filteredTasks = tasks.filter(t => t.frequence === freqTab)
  const pending = filteredTasks.filter(t => !t.validated)
  const done = filteredTasks.filter(t => t.validated)

  // Grouper par zone
  const grouped = (list: Task[]) => {
    const map: Record<string, Task[]> = {}
    for (const t of list) {
      const z = t.zone1 || 'Général'
      if (!map[z]) map[z] = []
      map[z].push(t)
    }
    return map
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {errorMsg && (
        <div className="fixed bottom-4 left-4 right-4 z-50 bg-red-600 text-white text-sm rounded-xl px-4 py-3 shadow-lg" onClick={() => setErrorMsg('')}>
          {errorMsg}
        </div>
      )}
      {/* Header */}
      <div className="bg-white border-b px-4 flex items-center gap-3 sticky top-0 z-10" style={{ paddingTop: 'max(16px, env(safe-area-inset-top))', paddingBottom: '16px' }}>
        <button onClick={() => navigate('/app/home')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList size={18} className="text-blue-600" /> UtileClean
          </h1>
          <p className="text-xs text-gray-500 capitalize">{format(new Date(), 'EEEE d MMMM', { locale: fr })}</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100">
          <RefreshCw size={16} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Sous-onglets fréquence */}
      <div className="bg-white border-b sticky top-[69px] z-10">
        <div className="flex overflow-x-auto px-3 py-2 gap-2 no-scrollbar">
          {FREQ_TABS.map(({ freq, label, color, inactive }) => {
            const total = tasks.filter(t => t.frequence === freq).length
            const doneCount = tasks.filter(t => t.frequence === freq && t.validated).length
            const active = freqTab === freq
            return (
              <button key={freq} onClick={() => setFreqTab(freq)}
                className={`flex-shrink-0 flex flex-col items-center px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${active ? color + ' text-white border-transparent' : 'bg-white border-gray-200 text-gray-600'}`}>
                <span>{label}</span>
                <span className={`text-xs mt-0.5 font-normal ${active ? 'text-white/75' : 'text-gray-400'}`}>
                  {total > 0 ? `${doneCount}/${total}` : '—'}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 py-3">
        <div className="bg-white rounded-2xl border p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Mes tâches — {FREQ_TABS.find(f => f.freq === freqTab)?.label.toLowerCase()}</p>
            <p className="text-2xl font-bold text-gray-900">{done.length} / {filteredTasks.length}</p>
          </div>
          <div className="w-14 h-14 relative">
            <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.9" fill="none"
                stroke={filteredTasks.length > 0 && done.length === filteredTasks.length ? '#16a34a' : '#2563eb'}
                strokeWidth="3"
                strokeDasharray={`${filteredTasks.length > 0 ? Math.round((done.length / filteredTasks.length) * 100) : 0} 100`}
                strokeLinecap="round" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700">
              {filteredTasks.length > 0 ? Math.round((done.length / filteredTasks.length) * 100) : 0}%
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 pb-8 space-y-4">
        {loading ? (
          <div className="text-center py-16 text-gray-400">
            <RefreshCw size={32} className="mx-auto mb-2 animate-spin text-blue-500" />
            <p className="text-sm">Chargement...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <CheckCircle2 size={56} className="mx-auto mb-3 text-gray-200" />
            <p className="font-medium">Aucune tâche {FREQ_TABS.find(f => f.freq === freqTab)?.label.toLowerCase()} aujourd'hui</p>
            <p className="text-sm mt-1">Tu n'as pas de tâche assignée dans cette catégorie</p>
          </div>
        ) : (
          <>
            {/* Tâches à faire */}
            {pending.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
                  À faire ({pending.length})
                </p>
                {Object.entries(grouped(pending)).map(([zone, zoneTasks]) => (
                  <div key={zone} className="mb-3">
                    <p className="text-xs font-medium text-gray-400 px-1 mb-1">{zone}</p>
                    <div className="space-y-2">
                      {zoneTasks.map(task => (
                        <div key={task.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                          <div className="p-4 space-y-3">

                            {/* Zone + Nom */}
                            <div>
                              {task.zone2 && (
                                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide leading-tight">
                                  {task.zone2}{task.zone3 ? ` › ${task.zone3}` : ''}
                                </p>
                              )}
                              <p className="font-bold text-gray-900 text-base leading-snug mt-0.5">{task.nom}</p>
                              {task.rotationActuelle && (
                                <p className="mt-1 inline-block text-sm font-bold bg-teal-100 text-teal-800 px-2.5 py-1 rounded-lg">
                                  ↻ Cette fois : {task.rotationActuelle}
                                </p>
                              )}
                              {task.dateLimite && (
                                <p className={`mt-1 text-xs font-semibold ${task.enRetard ? 'text-red-600' : 'text-pink-600'}`}>
                                  {task.enRetard ? '⚠ En retard, à faire aujourd’hui' : `À faire au plus tard le ${new Date(task.dateLimite).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`}
                                </p>
                              )}
                            </div>

                            {/* Photo lieu */}
                            {task.photoLieu && (
                              <div className="rounded-xl border border-green-200 overflow-hidden">
                                <div className="bg-green-50 flex items-center justify-center" style={{ height: 160 }}>
                                  <img src={task.photoLieu} alt="lieu de la tâche" className="h-full w-full object-cover" />
                                </div>
                                <div className="bg-white px-3 py-2 border-t border-green-100">
                                  <p className="text-xs text-green-600 font-medium uppercase tracking-wide">📍 Lieu de la tâche</p>
                                </div>
                              </div>
                            )}

                            {/* Produit — fiche pleine largeur */}
                            {(task.photoProduit || task.produit) && (
                              <div className="rounded-xl border border-slate-200 overflow-hidden">
                                {task.photoProduit && (
                                  <div className="bg-slate-100 flex items-center justify-center" style={{ height: 180 }}>
                                    <img src={task.photoProduit} alt={task.produit}
                                      className="h-full w-full object-contain p-3" />
                                  </div>
                                )}
                                <div className="bg-white px-3 py-2.5 flex items-center justify-between border-t border-slate-200">
                                  <div>
                                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Produit à utiliser</p>
                                    <p className="font-bold text-gray-900 text-base">{task.produit}</p>
                                  </div>
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${FREQ_COLORS[task.frequence] || 'bg-gray-100 text-gray-600'}`}>
                                    {FREQ_LABELS[task.frequence] || task.frequence}
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Protocole — étapes colorées */}
                            {task.consigne && (
                              <div>
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Protocole d'application</p>
                                <div className="space-y-1.5">
                                  {task.consigne.split('\n').filter(l => l.trim()).map((line, i) => (
                                    <div key={i} className={`flex items-start gap-2 rounded-xl px-3 py-2 border text-sm font-medium ${stepStyle(line)}`}>
                                      <span className="leading-snug">{line}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Badges secondaires */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {!task.photoProduit && !task.produit && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${FREQ_COLORS[task.frequence] || 'bg-gray-100 text-gray-600'}`}>
                                  {FREQ_LABELS[task.frequence] || task.frequence}
                                </span>
                              )}
                              {(task.creneauRetenu || task.creneauAttribution) !== 'toute_journee' && (
                                <span className="text-xs text-gray-400">
                                  {(task.creneauRetenu || task.creneauAttribution) === 'matin' ? '🌅 Matin' : '🌇 Après-midi'}
                                </span>
                              )}
                              {task.isShared && task.assignedTo && task.assignedTo.length > 1 && (
                                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                  👥 Avec {task.assignedTo.filter(a => a.id !== employeeId).map(a => a.name.split(' ')[0]).join(', ')}
                                </span>
                              )}
                            </div>

                            {/* Tâche QSE à formulaire : on remplit le relevé, la tâche se valide toute seule */}
                            {task.formulaire ? (
                              <button onClick={() => navigate(formRoute(task))}
                                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3.5 rounded-xl text-base">
                                {task.formulaire === 'reception' ? '🚚 Contrôler la livraison' : '🌡 Remplir le relevé'}
                              </button>
                            ) : (
                            /* Photo de preuve (facultative) + bouton valider */
                            <div className="flex gap-2">
                              <label className={`flex items-center justify-center gap-1 px-3 rounded-xl border text-sm font-medium cursor-pointer ${
                                photos[task.id] ? 'border-green-400 bg-green-50 text-green-700' : 'border-gray-200 text-gray-500'}`}>
                                {photos[task.id]
                                  ? <img src={photos[task.id]} alt="" className="w-8 h-8 rounded object-cover" />
                                  : <span>📷</span>}
                                <span>{photos[task.id] ? 'Photo' : 'Photo'}</span>
                                <input type="file" accept="image/*" capture="environment" className="hidden"
                                  onChange={e => pickPhoto(task.id, e.target.files?.[0])} />
                              </label>
                              <button
                                onClick={() => validate(task)}
                                disabled={validating === task.id}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl transition-colors disabled:opacity-50 text-base">
                                {validating === task.id
                                  ? <span className="flex items-center justify-center gap-2"><RefreshCw size={16} className="animate-spin" /> Validation...</span>
                                  : '✓ Valider cette tâche'}
                              </button>
                            </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Tâches validées */}
            {done.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">
                  Validées ({done.length})
                </p>
                <div className="space-y-2">
                  {done.map(task => (
                    <div key={task.id} className="bg-gray-100 rounded-2xl border border-gray-200 overflow-hidden opacity-70">
                      <div className="p-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 size={24} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            {task.zone1 && (
                              <p className="text-base font-bold text-gray-400 leading-tight line-through">{task.zone1}</p>
                            )}
                            {task.zone2 && (
                              <p className="text-sm font-semibold text-gray-400 leading-tight line-through">{task.zone2}{task.zone3 ? <span className="font-normal"> › {task.zone3}</span> : ''}</p>
                            )}
                            <p className="font-semibold text-gray-400 mt-1 line-through">{task.nom}</p>
                            {task.produit && (
                              <p className="text-xs text-gray-400 mt-1">Produit : {task.produit}</p>
                            )}
                            {task.consigne && (
                              <p className="text-xs text-gray-400 mt-1.5 bg-gray-200 rounded-lg px-2 py-1.5 line-through">
                                {task.consigne}
                              </p>
                            )}
                            {task.photoProduit && (
                              <div className="mt-2 opacity-50">
                                <img src={task.photoProduit} alt={task.produit || task.nom}
                                  className="w-32 h-44 object-cover rounded-xl border border-gray-300 grayscale" />
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-200 text-gray-500">
                                {FREQ_LABELS[task.frequence] || task.frequence}
                              </span>
                              {task.validatedAt && (
                                <span className="text-xs text-green-600 font-medium">
                                  ✓ {format(new Date(task.validatedAt), 'HH:mm')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tout validé */}
            {pending.length === 0 && done.length > 0 && (
              <div className="text-center py-6">
                <CheckCircle2 size={48} className="mx-auto mb-2 text-green-500" />
                <p className="font-bold text-green-700">Toutes les tâches sont faites !</p>
                <p className="text-sm text-gray-500 mt-1">Excellent travail 👏</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
